export default class LocalizationModal extends window.HTMLElement {
  dialog: HTMLDialogElement | null = null;
  overlay: HTMLDivElement | null = null;
  panel: HTMLDivElement | null = null;
  toggleBtn: HTMLElement | null = null;
  private previousActiveElement: HTMLElement | null = null;
  private focusableElements: HTMLElement[] = [];

  constructor() {
    super();
    this.dialog = this.querySelector('#localization-modal-dialog');
    this.overlay = this.querySelector('#localization-modal-overlay');
    this.panel = this.querySelector('#localization-modal-panel');
    this.toggleBtn = this.querySelector('#localization-modal-toggle');

    const closeBtns = this.querySelectorAll('[data-localization-modal-close]');

    this.toggleBtn?.addEventListener('click', () => {
      this.openDialog();
    });

    this.toggleBtn?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openDialog();
      }
    });

    closeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.close();
      });
    });

    this.overlay?.addEventListener('click', () => {
      this.close();
    });

    // Intercept native Escape close
    this.dialog?.addEventListener('cancel', (e) => {
      e.preventDefault(); // stop auto-close
      this.close(); // use animated version
    });

    // Handle keyboard navigation within modal
    this.dialog?.addEventListener('keydown', (e) => {
      this.handleKeydown(e);
    });
  }

  openDialog = () => {
    // Store the currently focused element
    this.previousActiveElement = document.activeElement as HTMLElement;

    // Update ARIA attributes
    this.toggleBtn?.setAttribute('aria-expanded', 'true');

    window.scrollLock();
    this.dialog?.showModal();
    this.panel?.classList.remove('translate-x-full');
    this.panel?.classList.add('translate-x-0');

    // Set up focus management
    this.setupFocusManagement();

    // Focus the first focusable element after animation
    setTimeout(() => {
      this.focusFirstElement();
    }, 100);
  };

  close() {
    // Update ARIA attributes
    this.toggleBtn?.setAttribute('aria-expanded', 'false');

    window.scrollUnlock();
    this.panel?.classList.remove('translate-x-0');
    this.panel?.classList.add('translate-x-full');

    // Return focus to the trigger button
    setTimeout(() => {
      this.dialog?.close();
      this.previousActiveElement?.focus();
      this.previousActiveElement = null;
    }, 300);
  }

  private setupFocusManagement() {
    // Get all focusable elements within the modal
    const focusableSelectors = [
      'button:not([disabled])',
      'select:not([disabled])',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    this.focusableElements = Array.from(this.dialog?.querySelectorAll(focusableSelectors) || []) as HTMLElement[];
  }

  private focusFirstElement() {
    const firstElement = this.focusableElements[0];
    if (firstElement) {
      firstElement.focus();
    }
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }

    if (e.key === 'Tab') {
      this.handleTabKey(e);
    }
  }

  private handleTabKey(e: KeyboardEvent) {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab: move backwards
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: move forwards
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }
}

if (!customElements.get('localization-modal')) {
  window.customElements.define('localization-modal', LocalizationModal);
}
